import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useSelector } from "react-redux";
import {
	Box,
	InputAdornment,
	Stack,
	Tab,
	Tabs,
	TextField,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

import CustomTable from "../../../custom-table";
import { selectEnhancedConditionsDocumentsList } from "../../../../features/enhanced-conditions-documents_list/enhancedConditionsDocumentsListSlice";
import { selectEnhancedConditionStatus } from "../../../../features/enhanced-condition-status/enhancedConditionStatusSlice";
import { useGetAttachmentsMutation } from "../../../../features/get-attachments/getAttachmentsApiSlice";
import { fetchLoanAttachmentDocuments } from "../../../temporary-drawer/components/enhanced-condition-detail/supporting-documents";

import styles from "../index.module.scss";

const topTabOptions = [
	{ key: "all", label: "All Conditions" },
	{ key: "open", label: "Open" },
	{ key: "closed", label: "Closed" },
];

const preferredCategories = ["Income", "Property", "Assets", "Legal"];
const preferredPriorTo = ["Docs", "Approval"];
const openSubStatuses = new Set([
	"added",
	"received",
	"requested",
	"re-requested",
	"rerequested",
	"rejected",
	"reviewed",
]);
const closedSubStatuses = new Set(["cleared", "waived", "fulfilled"]);

const normalizeValue = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase();

const normalizeStatusFilterValue = (value) => {
	return normalizeValue(value);
};

const getAttachmentAssignedEntityNames = (assignedTo) => {
	if (Array.isArray(assignedTo)) {
		return assignedTo
			.map((item) => normalizeValue(item?.entityName ?? item?.name))
			.filter(Boolean);
	}

	return [normalizeValue(assignedTo?.entityName ?? assignedTo?.name)].filter(
		Boolean
	);
};

const getAttachmentDocumentCount = (attachmentRow) => {
	const directCount = Number(attachmentRow?.count_of_doc);

	if (Number.isFinite(directCount) && directCount > 0) {
		return directCount;
	}

	if (Array.isArray(attachmentRow?.documents)) {
		return attachmentRow.documents.length || 1;
	}

	if (Array.isArray(attachmentRow?.attachments)) {
		return attachmentRow.attachments.length || 1;
	}

	return 1;
};

const buildEnhancedConditionCounts = ({
	conditions = [],
	attachments = [],
}) => {
	return (conditions || []).map((conditionRow) => {
		const conditionTitle = normalizeValue(
			conditionRow?.title ?? conditionRow?.conditionTitle
		);
		const matchingAttachmentRows = (attachments || []).filter((attachmentRow) => {
			if (attachmentRow?.isActive === false) {
				return false;
			}

			const assignedTitles = getAttachmentAssignedEntityNames(
				attachmentRow?.assignedTo
			);

			return assignedTitles.some(
				(assignedTitle) => assignedTitle === conditionTitle
			);
		});
		const matchingCount = matchingAttachmentRows.reduce(
			(total, attachmentRow) => total + getAttachmentDocumentCount(attachmentRow),
			0
		);

		return {
			...conditionRow,
			count_of_doc: matchingCount,
			supportingDocuments: matchingAttachmentRows,
			linkedDocuments: matchingAttachmentRows,
		};
	});
};

const EnhancedConditionTracking = ({
	documentsList = [],
	brokerUploadColumns,
	setDocumentsList,
	handleConditionUploadClick,
	handleConditionDownloadClick,
	toggleDrawer,
	loanId,
	onConditionListRefresh,
	conditionListRefreshRevision = 0,
	onConditionListRefreshApplied,
}) => {
	const allLoanDocuments = useSelector(selectEnhancedConditionsDocumentsList);
	const enhancedConditionsStatus = useSelector(selectEnhancedConditionStatus);
	const isEnhancedEnabled =
		String(enhancedConditionsStatus ?? "")
			.trim()
			.toUpperCase() === "Y";
	const [getAttachments] = useGetAttachmentsMutation();
	const [tableDocumentsList, setTableDocumentsList] = useState([]);
	const [activeTab, setActiveTab] = useState("all");
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const [categoryFilter, setCategoryFilter] = useState("all");
	const [priorToFilter, setPriorToFilter] = useState("all");
	const [loanAttachmentDocuments, setLoanAttachmentDocuments] = useState([]);
	const loanAttachmentDocumentsLoanIdRef = useRef("");
	const lastAppliedConditionListRefreshRevisionRef = useRef(0);

	const sourceDocumentsList = useMemo(() => {
		const docs = Array.isArray(documentsList) ? documentsList : [];
		const loanDocs = Array.isArray(allLoanDocuments) ? allLoanDocuments : [];

		return docs.map((doc) => {
			const matchedLoanDoc = loanDocs.find((loanDoc) =>
				(loanDoc?.conditions || []).some(
					(condition) =>
						String(condition?.entityId ?? condition?.document_id) ===
						String(doc?.id ?? doc?.document_id)
				)
			);

			return {
				...doc,
				lastAttachmentDate:
					matchedLoanDoc?.lastAttachmentDate ?? doc?.lastAttachmentDate ?? null,
			};
		});
	}, [allLoanDocuments, documentsList]);

	const sourceDocumentsWithCounts = useMemo(() => {
		const rows = Array.isArray(sourceDocumentsList) ? sourceDocumentsList : [];
		const attachments = Array.isArray(loanAttachmentDocuments)
			? loanAttachmentDocuments
			: [];

		if (isEnhancedEnabled) {
			return buildEnhancedConditionCounts({
				conditions: rows,
				attachments,
			});
		}

		return rows.map((row) => {
			const rowDocuments = Array.isArray(row?.documents) ? row.documents : [];

			const count_of_doc = rowDocuments.reduce((count, rowDocument) => {
				const entityId = String(rowDocument?.entityId ?? "").trim();

				if (!entityId) {
					return count;
				}

				const matchingDocuments = attachments.filter((attachment) => {
					const attachmentEntityId = String(
						attachment?.assignedTo?.entityId ?? ""
					).trim();

					return attachmentEntityId === entityId;
				});

				return count + matchingDocuments.length;
			}, 0);

			return {
				...row,
				count_of_doc,
			};
		});
	}, [isEnhancedEnabled, loanAttachmentDocuments, sourceDocumentsList]);

	useEffect(() => {
		setTableDocumentsList(sourceDocumentsWithCounts);
	}, [sourceDocumentsWithCounts]);

	useEffect(() => {
		if (!conditionListRefreshRevision) {
			return;
		}

		if (
			lastAppliedConditionListRefreshRevisionRef.current ===
			conditionListRefreshRevision
		) {
			return;
		}

		if (tableDocumentsList !== sourceDocumentsWithCounts) {
			return;
		}

		lastAppliedConditionListRefreshRevisionRef.current =
			conditionListRefreshRevision;
		onConditionListRefreshApplied?.(conditionListRefreshRevision);
	}, [
		conditionListRefreshRevision,
		onConditionListRefreshApplied,
		sourceDocumentsWithCounts,
		tableDocumentsList,
	]);

	useEffect(() => {
		if (!loanId) {
			loanAttachmentDocumentsLoanIdRef.current = "";
			setLoanAttachmentDocuments([]);
			return;
		}

		if (loanAttachmentDocumentsLoanIdRef.current === String(loanId)) {
			return;
		}

		loanAttachmentDocumentsLoanIdRef.current = String(loanId);
		let isMounted = true;

		const loadLoanAttachmentDocumentsList = async () => {
			try {
				const loanDocuments = await fetchLoanAttachmentDocuments({
					loanId,
					getAttachments,
				});
				setLoanAttachmentDocuments(loanDocuments);
			} catch (error) {
				if (!isMounted) {
					return;
				}

				console.warn(
					"[Enhanced Condition Tracking] attachment load failed:",
					error
				);
				setLoanAttachmentDocuments([]);
			}
		};

		loadLoanAttachmentDocumentsList();

		return () => {
			isMounted = false;
		};
	}, [getAttachments, loanId]);

	const handleSupportingDocumentsRefresh = useCallback(
		(conditionId, refreshedSupportingDocuments = []) => {
			const conditionKey = String(conditionId ?? "").trim();
			const nextSupportingDocuments = Array.isArray(
				refreshedSupportingDocuments
			)
				? refreshedSupportingDocuments
				: [];

			if (!conditionKey) {
				return;
			}

			setDocumentsList((previousDocuments) =>
				(previousDocuments || []).map((documentRow) => {
					const documentRowId = String(
						documentRow?.id ??
							documentRow?.entityId ??
							documentRow?.conditionId ??
							""
					);

					if (documentRowId !== conditionKey) {
						return documentRow;
					}

					return {
						...documentRow,
						supportingDocuments: nextSupportingDocuments,
						documents: nextSupportingDocuments,
						linkedDocuments: nextSupportingDocuments,
						...(isEnhancedEnabled
							? { count_of_doc: nextSupportingDocuments.length }
							: {}),
					};
				})
			);
		},
		[isEnhancedEnabled, setDocumentsList]
	);

	const availableStatuses = [
		"all",
		"Added",
		"Received",
		"Re-Requested",
		"Rejected",
		"Cleared",
		"Waived",
	];

	const availableCategories = useMemo(() => {
		const categories = new Set();
		tableDocumentsList.forEach((row) => {
			const category = String(row?.category ?? "").trim();
			if (category) {
				categories.add(category);
			}
		});

		const ordered = preferredCategories.filter((category) =>
			categories.has(category)
		);
		const extras = Array.from(categories).filter(
			(category) => !preferredCategories.includes(category)
		);

		return ["all", ...ordered, ...extras];
	}, [tableDocumentsList]);

	const availablePriorTo = useMemo(() => {
		const priorToValues = new Set();
		tableDocumentsList.forEach((row) => {
			const priorTo = String(row?.priorTo ?? "").trim();
			if (priorTo) {
				priorToValues.add(priorTo);
			}
		});

		const ordered = preferredPriorTo.filter((value) =>
			priorToValues.has(value)
		);
		const extras = Array.from(priorToValues).filter(
			(value) => !preferredPriorTo.includes(value)
		);

		return ["all", ...ordered, ...extras];
	}, [tableDocumentsList]);

	const filteredTableDocumentsList = useMemo(() => {
		const query = normalizeValue(searchQuery);
		const normalizedStatusFilter = normalizeStatusFilterValue(statusFilter);

		return tableDocumentsList.filter((row) => {
			const normalizedRowSubStatus = normalizeValue(row?.sub_status);
			const rawRowSubStatus = String(row?.sub_status ?? "").trim();
			const rowCategory = String(row?.category ?? "").trim();
			const rowPriorTo = String(row?.priorTo ?? "").trim();

			const matchesTab =
				activeTab === "all"
					? true
					: activeTab === "open"
						? openSubStatuses.has(normalizedRowSubStatus)
						: activeTab === "closed"
							? closedSubStatuses.has(normalizedRowSubStatus)
							: true;

			const matchesStatus =
				statusFilter === "all"
					? true
					: normalizedStatusFilter === "re-requested"
						? rawRowSubStatus === "Rerequested"
						: normalizedRowSubStatus === normalizedStatusFilter;
			const matchesCategory =
				categoryFilter === "all" ||
				normalizeValue(rowCategory) === normalizeValue(categoryFilter);
			const matchesPriorTo =
				priorToFilter === "all" ||
				normalizeValue(rowPriorTo) === normalizeValue(priorToFilter);

			const haystack = [
				row?.title,
				row?.category,
				row?.priorTo,
				row?.sourceOfCondition,
				row?.status,
				row?.sub_status,
				row?.createdDate,
				row?.assignedTo?.map((item) => item?.entityName).join(" "),
			]
				.map(normalizeValue)
				.join(" ");

			const matchesSearch = query ? haystack.includes(query) : true;

			return (
				matchesTab &&
				matchesStatus &&
				matchesCategory &&
				matchesPriorTo &&
				matchesSearch
			);
		});
	}, [
		activeTab,
		categoryFilter,
		priorToFilter,
		searchQuery,
		tableDocumentsList,
		statusFilter,
	]);

	const renderConditionTable = (rows) => (
		<>
			<CustomTable
				tableType="enhanced-condition-tracking"
				squaredBorder={true}
				maxHeight={true}
				columnData={brokerUploadColumns}
				rowData={rows}
				setSortedData={setTableDocumentsList}
				setDocumentsList={setDocumentsList}
				handleConditionUploadClick={handleConditionUploadClick}
				handleConditionDownloadClick={handleConditionDownloadClick}
				toggleDrawer={toggleDrawer}
				loanId={loanId}
				extraData={{
					loanAttachmentDocuments,
					updateLoanAttachmentDocuments: setLoanAttachmentDocuments,
					onSupportingDocumentsRefresh: handleSupportingDocumentsRefresh,
					onConditionListRefresh,
				}}
			/>
		</>
	);

	const renderChip = (label, isActive, onClick, key) => (
		<Box
			key={key}
			component="button"
			type="button"
			onClick={onClick}
			className={`${styles.filterChip} ${isActive ? styles.filterChipActive : ""}`}
		>
			{label}
		</Box>
	);

	return (
		<Stack direction="column" className={styles.bottomContainer}>
			<Stack direction="column" className={styles.enhancedToolbar}>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					className={styles.topBar}
				>
					<Tabs
						value={activeTab}
						onChange={(event, value) => setActiveTab(value)}
						className={styles.topTabs}
					>
						{topTabOptions.map((tab) => (
							<Tab
								key={tab.key}
								value={tab.key}
								className={styles.topTab}
								label={
									<Stack direction="row" alignItems="center" gap={1}>
										<span>{tab.label}</span>
									</Stack>
								}
							/>
						))}
					</Tabs>

					<Stack direction="row" alignItems="center" gap={1.5}>
						<TextField
							value={searchQuery}
							onChange={(event) => setSearchQuery(event.target.value)}
							placeholder="Search condition or category"
							size="small"
							className={styles.searchField}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchOutlinedIcon className={styles.searchIcon} />
									</InputAdornment>
								),
							}}
						/>
						<Box className={styles.conditionCount}>
							{filteredTableDocumentsList.length} conditions
						</Box>
					</Stack>
				</Stack>

				<Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}>
					<Stack direction="row" alignItems="center" gap={1}>
						<Box className={styles.filterLabel}>Status</Box>
						{availableStatuses.map((status) =>
							renderChip(
								status === "all" ? "All" : status,
								statusFilter === status,
								() => setStatusFilter(status),
								`status-${status}`
							)
						)}
					</Stack>

					<Stack direction="row" alignItems="center" gap={1}>
						<Box className={styles.filterLabel}>Category</Box>
						{availableCategories.map((category) =>
							renderChip(
								category === "all" ? "All" : category,
								categoryFilter === category,
								() => setCategoryFilter(category),
								`category-${category}`
							)
						)}
					</Stack>

					<Stack direction="row" alignItems="center" gap={1}>
						<Box className={styles.filterLabel}>Prior To</Box>
						{availablePriorTo.map((priorTo) =>
							renderChip(
								priorTo === "all" ? "All" : priorTo,
								priorToFilter === priorTo,
								() => setPriorToFilter(priorTo),
								`prior-to-${priorTo}`
							)
						)}
					</Stack>
				</Stack>
			</Stack>

			<Stack direction="column" className={styles.conditionTableStack}>
				{renderConditionTable(filteredTableDocumentsList)}
			</Stack>
		</Stack>
	);
};

export default EnhancedConditionTracking;
